import {Button, Card, Drawer, Grid, Layout, Menu, Modal, notification, theme as antdTheme, Tooltip} from 'antd';
import {LeftOutlined, MenuOutlined, MoonOutlined, RightOutlined, SunOutlined} from '@ant-design/icons';
import {useAppTheme} from '../../config/theme-context';
import {Outlet, useLocation, useNavigate} from 'react-router-dom';
import {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {roleMenuItems} from './constantRole';
import ProfileMenu from "./profile-menu";
import {useQueryClient} from "@tanstack/react-query";
import {useSelfProfile} from "./header-query";
import NotificationBell from "./notification-bell";
import {useStompClient, useSubscription} from "react-stomp-hooks";
import {installSoundUnlock, playNotificationSound, unlockNotificationSound} from "../../config/notification-sound";
import {
    disablePushNotifications,
    enablePushNotifications,
    getPermissionState,
    showLocalNotification,
} from "../../config/push-notifications";
import {onReturnToTab} from "../../config/visibility";
import {clearAuthSession} from "../../config/auth-session";
import {
    PASSWORD_SCREEN,
    acknowledgePasswordPrompt,
    isPasswordChangeRequired,
    isPasswordPromptAcknowledged,
} from "../../config/must-change-password";

const {Header, Content} = Layout;
const {useBreakpoint} = Grid;

const Dashboard = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const {t, i18n} = useTranslation();
    const screens = useBreakpoint();
    const {mode, toggleMode, branding} = useAppTheme();
    const {token: themeToken} = antdTheme.useToken();

    const [menuData, setMenuData] = useState([]);
    const [selectedMainKey, setSelectedMainKey] = useState('');
    const [selectedSubKey, setSelectedSubKey] = useState('');
    const [unreadNotifications, setUnreadNotifications] = useState(0);
    const [currentRole, setCurrentRole] = useState("OPERATOR");
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    // Whether the desktop side menus are folded away, to give the transcript the whole width.
    // Remembered across reloads and across screens: an operator who works with it collapsed is
    // telling us how they want their desk, and having to re-fold it every morning is a small
    // insult repeated daily.
    const [navCollapsed, setNavCollapsed] = useState(() => {
        try {
            return localStorage.getItem("npe-nav-collapsed") === "1";
        } catch (e) {
            return false;
        }
    });

    const toggleNav = () => {
        setNavCollapsed((prev) => {
            const next = !prev;
            try {
                localStorage.setItem("npe-nav-collapsed", next ? "1" : "0");
            } catch (e) {
                // storage denied — the choice simply does not survive a reload
            }
            return next;
        });
    };

    const [api, contextHolder] = notification.useNotification();
    const stompClient = useStompClient();

    // The chime is silent until the browser has seen a user gesture on this document. Arm the
    // unlock on the first click/key anywhere — an operator who signs in (or presses F5) and then
    // just waits used to get a silent first notification, and a silent second, until they happened
    // to touch the bell.
    useEffect(() => {
        installSoundUnlock();
    }, []);

    // A tap on an OS notification (push or the console's own) reaches us here as a message from the
    // service worker, and we route in-app. The worker used to call client.navigate(), a full
    // document load that dropped the socket and whatever the operator was typing.
    useEffect(() => {
        if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return undefined;
        const onMessage = (event) => {
            const data = event?.data;
            if (data && data.type === 'npe:open' && typeof data.url === 'string' && data.url.startsWith('/')) {
                navigate(data.url);
            }
        };
        navigator.serviceWorker.addEventListener('message', onMessage);
        return () => navigator.serviceWorker.removeEventListener('message', onMessage);
    }, [navigate]);

    /*
     * Web Push: make "you never have to refresh to know a customer wrote" true.
     *
     * The STOMP toast + chime below only reach a tab that is awake. A tab the operator is not looking
     * at is throttled and, after a few minutes, frozen — the socket dies and nothing rings until F5.
     * A push subscription lets the browser wake this device on its own. Operators only; admins and
     * supervisors are not the ones a customer is waiting on.
     *
     * Already granted (a returning operator): re-register silently on every login, so a device that
     * was cleared or re-issued a new endpoint is picked up without asking again. Never asked: offer
     * it once per session with a button, because browsers only show the permission prompt from a
     * click. Denied: say nothing — nagging does not change a "no".
     */
    useEffect(() => {
        // Read the role from storage directly rather than trusting `currentRole`: that state starts
        // as "OPERATOR" and is only corrected by a later effect, so on the first render an admin or
        // supervisor would otherwise be offered operator-only notifications.
        let role = currentRole;
        try {
            role = localStorage.getItem('role') || currentRole;
        } catch (e) { /* storage denied — fall back to state */ }
        if (role !== 'OPERATOR') return;
        const state = getPermissionState();
        if (state === 'granted') {
            enablePushNotifications();
            return;
        }
        if (state !== 'default') return;

        let asked = false;
        try {
            asked = localStorage.getItem('npe-push-offered') === '1';
        } catch (e) { /* storage denied — we may simply offer again */ }
        if (asked) return;

        // Marked as offered only on a DEFINITIVE outcome (subscribed, or the browser said no). A
        // transient failure — the worker could not be fetched, the push service was unreachable —
        // must be offered again on the next visit, not silently suppressed for the whole session.
        const markOffered = () => {
            try {
                localStorage.setItem('npe-push-offered', '1');
            } catch (e) { /* ignore */ }
        };

        const key = 'push-offer';
        api.info({
            key,
            message: 'اعلان پیام‌های جدید را فعال کنید',
            description: 'تا وقتی در پنجره‌ی دیگری هستید، رسیدن پیام مشتری با اعلان سیستم و صدای بلند به شما اطلاع داده شود.',
            placement: 'topRight',
            duration: 0,
            onClose: markOffered,
            btn: (
                <Button
                    type="primary"
                    size="small"
                    onClick={async () => {
                        // This click is the gesture the browser needs for the prompt — and for audio.
                        unlockNotificationSound();
                        api.destroy(key);
                        const result = await enablePushNotifications();
                        if (result === 'subscribed') {
                            markOffered();
                            // Let them hear what they just turned on.
                            playNotificationSound();
                            api.success({
                                message: 'اعلان‌ها فعال شد',
                                description: 'حتی وقتی این پنجره بسته باشد، پیام مشتری با صدا اعلان می‌شود.',
                                duration: 5,
                                placement: 'topRight',
                            });
                        } else if (result === 'notifications-only') {
                            // Permission granted; the console's own OS notifications and loud chime work
                            // whenever the tab is open. Only the closed-tab delivery (which rides Google's
                            // /Mozilla's push service) is unavailable on this network/browser. Say exactly
                            // that — it is a success, with one honest caveat.
                            markOffered();
                            playNotificationSound();
                            api.success({
                                message: 'اعلان‌ها فعال شد',
                                description: 'تا وقتی پنل باز است (حتی در پنجره یا تب دیگر) پیام مشتری با اعلان سیستم و صدای بلند اطلاع داده می‌شود. اعلان با پنجره‌ی کاملاً بسته در این مرورگر/شبکه در دسترس نیست.',
                                duration: 10,
                                placement: 'topRight',
                            });
                        } else if (result === 'denied') {
                            markOffered();
                            api.warning({
                                message: 'اجازه‌ی اعلان داده نشد',
                                description: 'می‌توانید بعداً از تنظیمات مرورگر (آیکن قفل کنار آدرس) آن را فعال کنید.',
                                duration: 6,
                                placement: 'topRight',
                            });
                        } else {
                            // 'failed' / 'unsupported': say so, and let the next visit offer again.
                            api.warning({
                                message: 'فعال‌سازی اعلان انجام نشد',
                                description: 'این مرورگر از اعلان سیستم پشتیبانی نمی‌کند یا خطایی رخ داد. پیام‌ها مثل قبل داخل پنل با صدا نمایش داده می‌شوند.',
                                duration: 8,
                                placement: 'topRight',
                            });
                        }
                    }}
                >
                    فعال‌سازی
                </Button>
            ),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentRole]);

    /*
     * Coming back to the tab after a long absence: assume the socket is a zombie and replace it.
     *
     * While a tab is frozen the heartbeat cannot run, so the connection can die without stompjs ever
     * finding out; on return the page still believes it is connected and quietly receives nothing.
     * Rather than wait for the heartbeat to notice (up to 50s) — or for the operator to give up and
     * press F5 — drop the socket ourselves. reconnectDelay brings a fresh one back in 3s and every
     * subscription re-attaches. The threshold keeps a quick alt-tab free of any blip; only an absence
     * long enough for the browser to have frozen the tab pays the 3s.
     */
    useEffect(() => {
        const LONG_ABSENCE_MS = 90 * 1000;
        return onReturnToTab((hiddenMs) => {
            if (hiddenMs < LONG_ABSENCE_MS || !stompClient) return;
            try {
                console.log(`[realtime] back after ${Math.round(hiddenMs / 1000)}s — refreshing the socket`);
                stompClient.forceDisconnect();
            } catch (e) {
                console.warn('[realtime] could not refresh the socket', e);
            }
        });
    }, [stompClient]);

    /*
     * Everyone whose password was chosen for them is sent here first.
     *
     * Until 2026-08-10 every operator account was created with the operator's own email address as
     * its password and nothing ever asked for a change — so on the first sign-in after that fix,
     * every account is in this state, including admins.
     *
     * Two parts, deliberately: a dialog that explains WHY (a redirect with no explanation reads as a
     * bug and generates a support call), and a redirect that keeps them on the password screen until
     * it is done. Signing out stays reachable the whole time — this must never be able to trap
     * somebody out of their own console.
     */
    const mustChangePassword = isPasswordChangeRequired();
    const [promptRead, setPromptRead] = useState(() => isPasswordPromptAcknowledged());
    const passwordPromptOpen = mustChangePassword && !promptRead;

    const dismissPasswordPrompt = () => {
        acknowledgePasswordPrompt();
        setPromptRead(true);
    };

    useEffect(() => {
        if (mustChangePassword && location.pathname !== PASSWORD_SCREEN) {
            navigate(PASSWORD_SCREEN, {replace: true});
        }
    }, [mustChangePassword, location.pathname, navigate]);


    const isRTL = (typeof document !== 'undefined' ? document.dir === 'rtl' : false);

    const HEADER_H = 64;
    const ACTION_SIZE = 32;

    const queryClient = useQueryClient();
    const {data: me} = useSelfProfile();

    const user = {
        fullName: me?.fullName,
        email: me?.email || '',
        avatarUrl: localStorage.getItem('avatar_url') || undefined,
    };

    useEffect(() => {
        if (!stompClient || currentRole !== 'OPERATOR') return;

        try {
            // Identity is taken from the authenticated STOMP session on the backend, so the body is
            // no longer an operatorId. Re-runs on (re)connect so counts reload after a drop.
            stompClient.publish({
                destination: "/app/notification/unread-count",
                body: "",
            });

            stompClient.publish({
                destination: "/app/notification/last",
                body: "",
            });

            console.log("📤 Requested notifications for current operator");
        } catch (e) {
            console.error("Error publishing notification requests", e);
        }
    }, [stompClient, currentRole]);

    useEffect(() => {
        const token = localStorage.getItem('access-token');
        if (token) {
            try {
                const role = localStorage.getItem("role") || 'OPERATOR';
                setMenuData(roleMenuItems[role] || []);
                setCurrentRole(role)
            } catch {
                setMenuData(roleMenuItems['OPERATOR'] || []);
            }
        } else {
            setMenuData(roleMenuItems['OPERATOR'] || []);
        }
    }, []);

    const activeMainItem = useMemo(
        () => menuData.find(m => m.key === selectedMainKey) || null,
        [menuData, selectedMainKey]
    );
    const activeHasChildren = !!(activeMainItem && activeMainItem.children && activeMainItem.children.length);

    useEffect(() => {
        if (!menuData.length) return;
        const path = location.pathname;
        const {main, sub} = findByPath(menuData, path);

        if (main) {
            setSelectedMainKey(main.key);
            setSelectedSubKey(sub ? sub.key : '');
        } else {
            const parents = menuData
                .filter(m => path.startsWith(m.path))
                .sort((a, b) => b.path.length - a.path.length);
            const parent = parents[0];
            if (parent) {
                setSelectedMainKey(parent.key);
                const child = (parent.children || [])
                    .filter(ch => path.startsWith(ch.path))
                    .sort((a, b) => b.path.length - a.path.length)[0];
                setSelectedSubKey(child ? child.key : '');
            } else {
                setSelectedMainKey(menuData[0].key);
                setSelectedSubKey('');
            }
        }
    }, [location.pathname, menuData]);

    // Per-user queues: Spring routes each to this session only. No operatorId in the destination,
    // so one operator can no longer subscribe to another's notification stream (which carries
    // customer identifiers and message text).
    const notificationCountTopic = "/user/queue/notification/unread-count";
    const lastNotificationTopic = "/user/queue/notification/last";

    useSubscription(notificationCountTopic, (messageStomp) => {
        try {
            const payload = JSON.parse(messageStomp.body);
            setUnreadNotifications(Number(payload) || 0);
        } catch (e) {
            console.error("Error parsing unread notification count", e);
        }
    });

    useSubscription(lastNotificationTopic, (messageStomp) => {
        try {
            const lastNotification = JSON.parse(messageStomp.body);
            if (!lastNotification) return;


            if (currentRole !== 'OPERATOR') return;

            // 🔗 مسیر ریدایرکت (fallback اگر نبود)
            const redirectPath =
                lastNotification.redirectUrl && typeof lastNotification.redirectUrl === "string"
                    ? lastNotification.redirectUrl
                    : "/dashboard/notifications";

            // Is the operator actually looking at the console right now? A hidden tab, a minimised
            // window, or focus in another app all count as "not looking".
            const looking = document.visibilityState === "visible" && document.hasFocus();

            // 🔊 پخش صدا — طبق انتخاب خود اپراتور (صدا و شدت آن، در تنظیمات حساب کاربری).
            // Twice when they are not looking: one ring in a busy office is "was that mine?", two
            // is getting up to check.
            playNotificationSound({repeat: looking ? 1 : 2});

            // The console's OWN system-level alert, no push service involved: when the tab is alive
            // but unfocused, the in-app toast below is invisible, so raise an OS notification (which
            // also plays the system sound on top of the chime). Tagged per chat so it collapses
            // with any Web Push for the same conversation instead of showing twice.
            if (!looking) {
                const chatMatch = /\/chats\/([^/?#]+)/.exec(redirectPath);
                showLocalNotification({
                    title: lastNotification.title || "پیام جدید دریافت شد.",
                    body: lastNotification.message || "",
                    url: redirectPath,
                    tag: chatMatch ? `chat-${chatMatch[1]}` : "npe-chat",
                    onClick: () => navigate(redirectPath),
                });
            }

            // یک key برای کنترل نوتیف
            const notifKey = `notification-${lastNotification.id || Date.now()}`;

            api.info({
                key: notifKey,
                message:
                    lastNotification.title ||
                    t("dashboard.new_chat_assigned") ||
                    "پیام جدید دریافت شد.",
                description: lastNotification.message || "",
                placement: isRTL ? "topLeft" : "topRight",
                duration: 6,
                // دکمه پایین نوتیف: "مشاهده جزئیات"
                btn: (
                    <Button
                        type="link"
                        size="small"
                        style={{padding: 0}}
                        onClick={() => {
                            navigate(redirectPath);
                            api.destroy(notifKey);
                        }}
                    >
                        مشاهده جزئیات
                    </Button>
                ),
                // اگر خود نوتیف کلیک شد (نه فقط دکمه)
                onClick: () => {
                    navigate(redirectPath);
                    api.destroy(notifKey);
                },
            });
        } catch (e) {
            console.error("Error parsing last notification", e);
        }
    });
    const handleBellClick = () => {
        // A real click is the browser's cue that audio is permitted; take it, so the first
        // notification of the shift is actually audible.
        unlockNotificationSound();
        navigate('/dashboard/notifications');
    };


    const findByPath = (items, path) => {
        for (const it of items) {
            if (it.path === path) return {main: it, sub: null};
            if (Array.isArray(it.children)) {
                for (const ch of it.children) {
                    if (ch.path === path) return {main: it, sub: ch};
                }
            }
        }
        return {main: null, sub: null};
    };

    const onMainClick = (item) => {
        setSelectedMainKey(item.key);
        if (item.children && item.children.length) {
            const first = item.children[0];
            setSelectedSubKey(first.key);
            navigate(first.path);
        } else {
            setSelectedSubKey('');
            navigate(item.path);
        }
        setMobileMenuOpen(false); // responsive-only: close drawer on select
    };

    const renderMainMenu = () => (
        <Menu
            mode="vertical"
            selectedKeys={[selectedMainKey]}

            items={menuData.map(item => ({
                key: item.key,
                icon: item.icon,
                label: t(item.label)
            }))}
            onClick={({key}) => {
                const item = menuData.find(i => i.key === key);
                if (item) onMainClick(item);
            }}
            // Fixed 200/260px menus are fine in the desktop column but overflow the drawer on a
            // narrow phone, where the drawer itself is only ~280px. In the drawer they just take
            // the width they are given.
            style={{width: screens.md ? 200 : "100%"}}
        />
    );

    const renderSubMenu = () => {
        if (!activeHasChildren) return null;
        const children = activeMainItem.children || [];
        const baseMenu = (
            <Menu
                mode="vertical"
                selectedKeys={selectedSubKey ? [selectedSubKey] : []}

                items={children.map(ch => ({
                    key: ch.key,
                    icon: ch.icon,
                    label: t(ch.label)
                }))}
                onClick={({key}) => {
                    const ch = children.find(c => c.key === key);
                    if (ch) {
                        setSelectedSubKey(ch.key);
                        navigate(ch.path);
                    }
                    setMobileMenuOpen(false); // responsive-only
                }}
                style={{width: screens.md ? 260 : "100%", marginInlineEnd: screens.md ? 8 : 0}}
            />
        );
        return <div>{baseMenu}</div>;
    };

    const onLogout = async () => {
        // Take this browser off the operator's push list BEFORE the token is gone (the unsubscribe
        // call needs it). Otherwise a shared desk PC keeps popping customers' messages as OS
        // notifications to whoever walks past, long after the operator has signed out. Best-effort
        // and bounded: a slow push service must never be able to hang the logout button.
        try {
            await Promise.race([
                disablePushNotifications(),
                new Promise((resolve) => setTimeout(resolve, 3000)),
            ]);
        } catch (e) { /* the server row is pruned on its next failed send anyway */ }
        await queryClient.invalidateQueries();
        // Only the session. localStorage also holds the operator's device preferences (sound,
        // folded nav) which have nothing to do with who is signed in.
        clearAuthSession();
        navigate('/login');
    };

    // --npe-app-h is the screen's real visible height (see config/viewport.js). 100dvh is the
    // fallback for browsers without visualViewport; plain 100vh is wrong on a phone, where it means
    // "the screen with the address bar hidden" and pushes the bottom of the app — on the chat
    // screen, the reply box — out of sight under the browser chrome.
    const APP_H = "var(--npe-app-h, 100dvh)";

    return (
        <Layout style={{minHeight: APP_H}}>
            {contextHolder}

            <Header
                style={{
                    padding: screens.xs ? '0 6px' : '0 24px',
                    // Theme token, not a hardcoded #fff — otherwise the header stays white in dark mode.
                    background: themeToken.colorBgContainer,
                    borderBottom: `1px solid ${themeToken.colorBorderSecondary}`,
                    position: 'relative',
                    display: 'grid',
                    gridTemplateColumns: screens.md ? 'auto 1fr auto' : '1fr auto',
                    gridTemplateAreas: screens.md
                        ? `'left center right'`
                        : `'left right'
                            'center center'`,
                    alignItems: 'center',
                    columnGap: 8,
                    rowGap: 8,
                    minHeight: 64,
                }}
            >
                {/* LEFT: burger + logo */}
                <div style={{
                    gridArea: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                }}>
                    {!screens.md && (
                        <Button
                            aria-label="menu"
                            icon={<MenuOutlined/>}
                            onClick={() => setMobileMenuOpen(true)}
                        />
                    )}
                    <a
                        href="https://npemarket.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{display: screens.md ? 'block' : 'none'}}
                    >
                        {/* Admin-uploaded console logo wins; /logo.png is the built-in fallback. */}
                        <img
                            src={branding?.consoleLogo || "/logo.png"}
                            alt={branding?.brandName || "Logo"}
                            style={{height: 40, marginInlineStart: 12, objectFit: "contain", maxWidth: 180}}
                        />
                    </a>
                </div>

                {/* RIGHT: wallet + bell + profile */}
                <div style={{
                    gridArea: 'right',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    marginTop: screens.xs ? '8px' : '0px',
                }}>

                    <Tooltip title={mode === 'dark' ? 'حالت روشن' : 'حالت تاریک'}>
                        <Button
                            aria-label="toggle theme"
                            shape="circle"
                            icon={mode === 'dark' ? <SunOutlined/> : <MoonOutlined/>}
                            onClick={toggleMode}
                        />
                    </Tooltip>

                    {currentRole === 'OPERATOR' &&
                        (
                            <NotificationBell
                                count={unreadNotifications}
                                title={t('dashboard.notifications') || 'اعلان‌ها'}
                                onClick={handleBellClick}
                                size={ACTION_SIZE}
                            />
                        )
                    }

                    <ProfileMenu
                        user={user}
                        size={ACTION_SIZE}
                        onLogout={onLogout}
                        onGoProfile={() => navigate('/dashboard/settings/profile')}
                        onGoBusinessInfo={() => navigate('/dashboard/settings/business-info')}
                        onGoNotifications={() => navigate('/dashboard/settings/notifications')}
                        onOpenSettings={() => navigate('/dashboard/settings')}
                        onHelp={() => window.open('https://sazmanyar.ir/help', '_blank')}
                    />
                </div>
            </Header>


            {/* The 14px top margin that used to be here at xs was added on top of a box already
                sized to fill everything below the header, so the page was permanently 14px taller
                than the screen and rocked under the thumb on every scroll. The header owns its own
                spacing; the content just takes what is left. */}
            <Content
                style={{
                    padding: 0,
                    height: `calc(${APP_H} - ${HEADER_H}px)`,
                    overflow: "hidden",
                }}
            >
                <div
                    style={{
                        height: "100%",
                        // A phone has 375px to spend. Nested padding here plus the card's own
                        // padding was taking 40 of them — 11% of the screen — before a single word
                        // of a customer's message was drawn.
                        padding: screens.md ? 10 : 4,
                        boxSizing: "border-box",
                        overflow: "hidden"
                    }}
                >
                    <Card
                        style={{height: "100%", margin: 0, direction: "rtl"}}
                        bodyStyle={{height: "100%", padding: screens.md ? 12 : 6}}
                        bordered
                    >
                        <div
                            style={{
                                display: "flex",
                                flexDirection: isRTL ? "row-reverse" : "row",
                                gap: 8,
                                height: "100%",
                                minHeight: 0,
                                overflow: "hidden"
                            }}
                        >
                            {/* Outlet scrolls */}
                            <div
                                style={{
                                    flex: 1,
                                    minWidth: 0,
                                    minHeight: 0,
                                    overflowY: "auto",
                                    overflowX: "hidden",
                                    overscrollBehavior: "contain"
                                }}
                            >
                                <Outlet/>
                            </div>

                            {/* hide static side menus on small screens */}
                            {!screens.md && null}

                            {screens.md && !navCollapsed && activeHasChildren && (
                                <div style={{flex: "0 0 220px", overflow: "visible"}}>
                                    {renderSubMenu()}
                                </div>
                            )}

                            {screens.md && !navCollapsed && (
                                <div style={{flex: "0 0 220px", overflow: "visible"}}>
                                    {renderMainMenu()}
                                </div>
                            )}

                            {/* The fold/unfold handle. Sits at the menus' edge — where the thing it
                                controls is — rather than in the header, so it reads as part of the
                                panel and stays reachable once the panel is gone. */}
                            {screens.md && (
                                <div
                                    style={{
                                        flex: "0 0 auto",
                                        display: "flex",
                                        alignItems: "flex-start",
                                        paddingTop: 4,
                                    }}
                                >
                                    <Tooltip
                                        title={navCollapsed ? "نمایش منو" : "جمع‌کردن منو برای فضای بیشتر"}
                                        placement={isRTL ? "left" : "right"}
                                    >
                                        <Button
                                            type="text"
                                            aria-label={navCollapsed ? "نمایش منو" : "جمع‌کردن منو"}
                                            aria-expanded={!navCollapsed}
                                            onClick={toggleNav}
                                            icon={
                                                // The arrow points the way the panel will move, in
                                                // whichever direction the interface is running.
                                                navCollapsed === isRTL
                                                    ? <LeftOutlined/>
                                                    : <RightOutlined/>
                                            }
                                            style={{
                                                height: 32,
                                                width: 24,
                                                minWidth: 24,
                                                padding: 0,
                                                color: themeToken.colorTextSecondary,
                                            }}
                                        />
                                    </Tooltip>
                                </div>
                            )}
                        </div>
                    </Card>
                </div>
            </Content>

            <Drawer
                open={mobileMenuOpen}
                onClose={() => setMobileMenuOpen(false)}
                placement={isRTL ? "right" : "left"}
                // Capped to 88vw for narrow phones by a stylesheet rule rather than a measurement
                // taken at render time, which would go stale when the phone is rotated.
                width={300}
                bodyStyle={{padding: 0}}
            >
                <div style={{padding: 12}}>
                    {renderMainMenu()}
                    {renderSubMenu()}
                </div>
            </Drawer>

            {/* Explains the redirect. No close button and no mask-click dismissal — the point is that
                it is read, not clicked past — but it is only a dialog: signing out from the header
                still works, so nobody can be locked out of their own console by it. */}
            <Modal
                open={passwordPromptOpen}
                title="یک رمز عبور شخصی انتخاب کنید"
                closable={false}
                maskClosable={false}
                keyboard={false}
                onOk={dismissPasswordPrompt}
                okText="انتخاب رمز عبور"
                cancelButtonProps={{style: {display: "none"}}}
                centered
            >
                <p style={{marginTop: 0}}>
                    رمز عبور فعلی حساب شما را شخص دیگری تعیین کرده است — یا مدیر سیستم آن را برای شما
                    ساخته، یا از رمز اولیه‌ی حساب استفاده می‌کنید.
                </p>
                <p style={{marginBottom: 0}}>
                    برای اینکه دسترسی به حساب شما فقط در اختیار خودتان باشد، لطفاً همین حالا یک رمز
                    عبور تازه انتخاب کنید. تا وقتی این کار انجام نشود، به همین صفحه هدایت می‌شوید.
                </p>
            </Modal>
        </Layout>
    );
};

export default Dashboard;
